<template>
  <UIPreloader v-if="isInit" message="Loading security info..." class="asset-drawer-security-master__preloader" />
  <div v-else class="asset-drawer-security-master">
    <div
      v-if="isSecurityInfoAssigned"
      class="asset-drawer-security-master__info"
    >
      <span>
        Symbol:
        <span class="font-medium">{{ securityInfoTicker }}</span>
      </span>
      <UIButton
        fill="light"
        size="xs"
        label="Reset"
        @click="handleClickReset"
      />
    </div>
    <form v-else class="asset-drawer-security-master__form" @submit="onSubmit">
      <EODSearch
        ref="eodInputRef"
        v-model="securityValue"
        :readonly
        :disabled="isLoading"
        label="Symbol"
        placeholder="Security master ticker"
        float
        short
        class="asset-drawer-security-master__input"
      />
      <UIButton
        v-if="isSaveButtonShown"
        :disabled="isLoading"
        :loading="isLoading"
        data-refid="assetSecurityMasterFormSave"
        label="Save"
      />
      <UIButton
        v-if="isCancelButtonShown"
        variant="gray"
        fill="outlined"
        data-refid="assetSecurityMasterFormCancel"
        label="Cancel"
        @click="onCancel"
      />
    </form>
    <div
      v-if="isSecurityInfoAssigned"
      class="asset-drawer-security-master__json"
    >
      <UIJsonTree :json size="sm">JSON data</UIJsonTree>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  inject,
  markRaw,
  nextTick,
  onMounted,
  reactive,
  ref,
  useTemplateRef,
  watch,
} from 'vue'
import { useField, useForm } from 'vee-validate'

import { AssetClass } from '..'
import { AssetSecurityMaster, ReadonlyMode } from '@types'

import { ASSET_FIELD } from '../utils/const'
import { READONLY_MODE } from '@/const/common'

import { rules } from '@/helpers/validate'

import { useAssetsStore } from '@/store/assets'
import { useModalsStore } from '@/store/modals'

import EODSearch from '@/modules/EODSearch/EODSearch.vue'

import { UIButton } from '@ui/buttons'
import { UIJsonTree, UIPreloader } from '@ui/core'
import AssetDrawerSecurityMasterDialog from './AssetDrawerSecurityMasterDialog.vue'

type Props = {
  instance: AssetClass
}

const props = defineProps<Props>()

const assetsStore = useAssetsStore()
const modalsStore = useModalsStore()

const eodInputRef = useTemplateRef('eodInputRef')

const securityValue = ref<AssetSecurityMaster>()

const change = reactive({
  type: true,
  name: true,
  ticker: true,
})

const readonly = inject<ReadonlyMode>(READONLY_MODE)

const isInit = computed(() => assetsStore.loadingSecurityInfo)
const isLoading = computed(() => assetsStore.loadingAction)

const assetType = props.instance.field<string>(ASSET_FIELD.TYPE)
const assetName = props.instance.field<string>(ASSET_FIELD.NAME)
const assetTicker = props.instance.field<string>(ASSET_FIELD.TICKER)
const assetSecurityInfoTicker = props.instance.field<string | null>(ASSET_FIELD.SECURITY_INFO_TICKER)

const securityInfo = computed(() => props.instance.securityInfo)

const securityInfoData = computed(
  () => securityInfo.value?.data || {},
)

const securityInfoTicker = computed(() => securityInfo.value?.symbol)

const isSecurityInfoAssigned = computed(
  () => securityInfoTicker.value && securityInfoData.value,
)

const isSaveButtonShown = computed(
  () => meta.value.dirty && meta.value.valid && !readonly?.value,
)

const isCancelButtonShown = computed(
  () => isSaveButtonShown.value && !isLoading.value,
)

const json = computed(() =>
  Object.fromEntries(
    Object.entries(securityInfoData.value).filter(
      ([, value]) => typeof value !== 'object',
    ),
  ),
)

const {
  handleSubmit,
  meta,
  resetForm
} = useForm<{
  security_info_ticker: string
  asset_type: string
  asset_name: string
  asset_ticker: string
}>({
  initialValues: {
    security_info_ticker: securityInfoTicker.value,
    asset_type: assetType.value,
    asset_name: assetName.value,
    asset_ticker: assetTicker.value,
  },
  validationSchema: {
    security_info_ticker: rules.required,
    asset_type: () => true,
    asset_name: () => true,
    asset_ticker: () => true,
  },
})

const { value: assetSecurityInfoTickerValue } = useField<string>('security_info_ticker')
const { value: assetTypeValue } = useField<string>('asset_type')
const { value: assetNameValue } = useField<string>('asset_name')
const { value: assetTickerValue } = useField<string>('asset_ticker')

const onSubmit = (e: Event) => {
  e.preventDefault()
  if (
    (assetType.value === assetTypeValue.value &&
      assetName.value === assetNameValue.value &&
      assetTicker.value === assetTickerValue.value) ||
    !assetSecurityInfoTickerValue.value
  ) {
    handleSave(false)()
  } else {
    handleShowDialog()
  }
}

const handleShowDialog = () => {
  const key = 'asset-drawer-security-master-dialog'
  const modalInstance = modalsStore.init(key, markRaw(AssetDrawerSecurityMasterDialog))
  modalInstance?.open(modalsStore.getZIndex(), {
    instance: props.instance,
    'model-value': change,
    oldType: assetType.value,
    oldName: assetName.value,
    oldTicker: assetTicker.value,
    newType: assetTypeValue.value,
    newName: assetNameValue.value,
    newTicker: assetTickerValue.value,
    onSave: () => {
      handleSave()()
    },
  })
}

const handleSave = (update = true) =>
  handleSubmit(async values => {
    if (change.type && update) {
      assetType.value = values.asset_type
    }
    if (change.name && update) {
      assetName.value = values.asset_name
    }
    if (change.ticker && update) {
      assetTicker.value = values.asset_ticker
    }
    assetSecurityInfoTicker.value = values.security_info_ticker
    await props.instance.update()
    fetchSecurityInfo()
    resetForm()
  })

const onCancel = async () => {
  if (securityInfo.value) {
    securityValue.value = {
      code: securityInfo.value.ticker,
      name: securityInfo.value.symbol,
      type: securityInfo.value.type,
      currency: '',
      exchange: '',
    }
  }
  if (readonly?.value) return
  eodInputRef.value?.cancel(
    securityInfo.value?.symbol
      ? securityInfo.value.symbol
      : undefined,
  )
  await nextTick()
  resetForm()
}

const handleClickReset = async () => {
  props.instance.securityInfo = undefined
  props.instance.field<string | null>(ASSET_FIELD.LINKED_SECURITY_ID).value = null
  assetSecurityInfoTicker.value = null
  props.instance.update()
  resetForm()
  await nextTick()
  eodInputRef.value?.focus()
}

const fetchSecurityInfo = async () => {
  const securityInfoId = props.instance.field<string>(ASSET_FIELD.LINKED_SECURITY_ID).value
  if (!securityInfoId || securityInfo.value) return
  const result = await assetsStore.fetchSecurityInfo(securityInfoId)
  props.instance.securityInfo = result
}

watch(securityValue, value => {
  if (!value) return
  assetSecurityInfoTickerValue.value = `${value.code}.${value.exchange}`
  assetTypeValue.value = value.type
  assetNameValue.value = value.name
  assetTickerValue.value = value.code
})

watch(eodInputRef, onCancel)

onMounted(fetchSecurityInfo)
</script>

<style>
.asset-drawer-security-master {
  @apply flex flex-col flex-auto;

  &__preloader {
    @apply flex-auto;
  }

  &__info {
    @apply flex items-center justify-between;
    @apply gap-2 py-4 px-default;
    @apply text-base sm:text-sm;
  }

  &__form {
    @apply flex flex-col sm:flex-row sm:items-end;
    @apply gap-2 py-4 px-default;
  }

  &__input {
    @apply flex-auto;
  }

  &__json {
    @apply flex flex-col flex-auto;
    @apply px-default;

    .ui-tree__search {
      @apply mb-4;
    }

    .ui-tree__container {
      @apply pb-4;
    }
  }
}
</style>
